import { Checkbox } from '@1trading/react/components/ui/checkbox';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { useInstitutionDetailsV2Answers } from '@/features/institution/hooks/useInstitutionDetails';
import type { InstitutionTaxSelfCertificationOrDefaults } from '@/features/institution/tax-self-certification/schema';

const DECLARATIONS = ['authorisedToSign', 'accurate', 'consentToReporting', 'undertakeToNotifyChanges'] as const;

const InstitutionTaxSelfCertificationFieldsetDeclarationLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.taxSelfCertification.declaration.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.taxSelfCertification.declaration.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionTaxSelfCertificationFieldsetDeclarationLegend.displayName = 'InstitutionTaxSelfCertificationFieldsetDeclarationLegend';

// A sole proprietor signs for themselves rather than on behalf of an entity
const InstitutionTaxSelfCertificationFieldsDeclarations = ({ context }: { context?: 'proprietor' }) => {
  const form = useFormContext<Pick<InstitutionTaxSelfCertificationOrDefaults, 'declaration'>>();
  const { t } = useTranslation();

  return DECLARATIONS.map((declaration) => (
    <FormField
      key={declaration}
      control={form.control}
      name={`declaration.${declaration}`}
      render={({ field }) => (
        <FormItem>
          <div className="flex flex-row items-center gap-4">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} />
            </FormControl>
            <FormLabel>{t(`institutionDetails.taxSelfCertification.declaration.${declaration}`, { context })}</FormLabel>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  ));
};

InstitutionTaxSelfCertificationFieldsDeclarations.displayName = 'InstitutionTaxSelfCertificationFieldsDeclarations';

const InstitutionTaxSelfCertificationFieldsetDeclaration = () => {
  const form = useFormContext<InstitutionTaxSelfCertificationOrDefaults>();
  const { data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { t } = useTranslation();
  const signatoryPartyId = form.watch('signatory.partyId');
  const management = institutionDetailsAnswers?.ownership?.management ?? [];

  const selectSignatory = (partyId: string) => {
    const signatory = management.find((person) => person.partyId === partyId);
    if (!signatory) return;

    form.setValue('signatory.partyId', signatory.partyId, { shouldValidate: true });
    form.setValue('signatory.fullName', `${signatory.firstName} ${signatory.lastName}`);
  };

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="signatory.partyId"
        render={() => (
          <FormItem>
            <FormLabel>{t('institutionDetails.taxSelfCertification.declaration.signatory')}*</FormLabel>
            <FormDescription>{t('institutionDetails.taxSelfCertification.declaration.signatoryDescription')}</FormDescription>
            <FormControl>
              <RadioGroup value={signatoryPartyId} variant="cards" aria-label={t('institutionDetails.taxSelfCertification.declaration.signatory')} onValueChange={selectSignatory}>
                {management.map((person) => (
                  <FormItem key={person.partyId}>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value={person.partyId} />
                      </FormControl>
                      <FormLabel contained>
                        {person.firstName} {person.lastName}
                      </FormLabel>
                    </RadioGroupItemCard>
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="signatory.capacity"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.taxSelfCertification.declaration.signatoryCapacity')}*</FormLabel>
            <FormControl>
              <Input {...field} placeholder={t('institutionDetails.taxSelfCertification.placeholders.signatoryCapacity')} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <InstitutionTaxSelfCertificationFieldsDeclarations />
    </div>
  );
};
InstitutionTaxSelfCertificationFieldsetDeclaration.displayName = 'InstitutionTaxSelfCertificationFieldsetDeclaration';

export { InstitutionTaxSelfCertificationFieldsDeclarations, InstitutionTaxSelfCertificationFieldsetDeclaration, InstitutionTaxSelfCertificationFieldsetDeclarationLegend };
